import React, { useEffect, useRef, useState } from 'react';
import { Card, Button, Row, Col, Table, Spinner, Badge, Form, Modal, Alert, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';

import ApplicantDocPreview from './ApplicantDocPreview';
import ApplicantSelect, { ApplicantOption } from './ApplicantSelect';
import { usePageFileDrop } from '../hooks/usePageFileDrop';
import {
  getApplicantsId,
  getApplicantsIdDocumentsDocumentId,
  patchApplicantsId,
  putApplicantsIdEmails,
  deleteApplicantsId,
  postApplicantsIdDocuments,
  deleteApplicantsIdDocumentsDocumentId,
  postApplicantsIdDocumentsDocumentIdReassign,
  postApplicantsIdContactAcknowledge,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type { Applicant, ApplicantDocument, ApplicantEmail, ContactField, ContactFieldSource, JsonPatchOperation } from '../api/generated/model';

interface EmailRow { id?: string; email: string; is_primary: boolean }

const CONTACT_FIELD_LABELS: Record<string, string> = {
  first_name: 'First name',
  last_name: 'Last name',
  phone: 'Phone',
  address: 'Address',
  linkedin: 'LinkedIn',
  github: 'GitHub',
};

const applicantEmails = (a: Applicant): ApplicantEmail[] =>
  a.emails && a.emails.length > 0 ? a.emails : [{ id: 'primary', email: a.email, is_primary: true }];

const fmtDate = (s?: string) => (s ? new Date(s).toLocaleString(undefined, { timeZoneName: 'short' }) : '');

const contactNorm = (s?: string) => (s || '').trim().toLowerCase();

const docTypeLabel = (t: string) => (t === 'cover_letter' ? 'cover letter' : t === 'resume' ? 'resume' : t);

// A row in the contact-review list: one distinct value plus which document types
// carry it (with counts). isCurrent marks the value in force (override or the
// single agreed doc value) — even when no document supplies it.
interface ReviewRow { value: string; sources: ContactFieldSource[]; isCurrent: boolean }

// renderContactReview surfaces, per contact field, the distinct values on file —
// one ROW per value, including the current value (so the user sees what they'd be
// keeping). The current row offers "Keep" (acknowledge, no change); every other
// row offers "Replace" (set it as the value) — or "Use" when there is no current
// value yet. Each value lists the document types that carry it, with a superscript
// count of how many documents of that type agree. A field appears when it has more
// than one value on file OR is flagged for review.
function renderContactReview(
  fields: ContactField[] | undefined,
  busy: boolean,
  acknowledge: (field: string, value?: string) => void,
  forget: (field: string) => void,
) {
  // Show any field that has more than one distinct value on file (documents +
  // the current value) so the user can always revisit or change their choice —
  // NOT only while it needs review. Acknowledging a field clears its review flag
  // but must NOT make the picker disappear (they may have picked wrong, or want to
  // switch later).
  const review = (fields || []).filter((f) => {
    const vals = new Set((f.candidates || []).map((c) => contactNorm(c.value)));
    if (f.effective) vals.add(contactNorm(f.effective));
    return vals.size > 1 || f.needs_review;
  });
  if (review.length === 0) return null;
  return (
    <div className="mt-3 border-top pt-2">
      <div className="text-muted small mb-2"><i className="bi bi-collection me-1" />Contact values to review</div>
      {review.map((f) => {
        const label = CONTACT_FIELD_LABELS[f.field] || f.field;
        const eff = f.effective;
        const rows: ReviewRow[] = (f.candidates || []).map((c) => ({
          value: c.value,
          sources: c.sources || [],
          isCurrent: !!eff && contactNorm(c.value) === contactNorm(eff),
        }));
        // The current value isn't always one of the documents (e.g. a curated/frozen
        // override like "Tom") — show it as its own row so it can be kept.
        if (eff && !rows.some((r) => r.isCurrent)) {
          rows.push({ value: eff, sources: [], isCurrent: true });
        }
        // Stable order INDEPENDENT of which row is current, so picking a value never
        // reorders the list: document-backed values first, then by value. (Being
        // "current" only changes the button, never the position.)
        rows.sort((a, b) => {
          const ad = a.sources.length > 0 ? 0 : 1;
          const bd = b.sources.length > 0 ? 0 : 1;
          if (ad !== bd) return ad - bd;
          return a.value.toLowerCase().localeCompare(b.value.toLowerCase());
        });
        const hasCurrent = !!eff;
        return (
          <div key={f.field} className="mb-3">
            <div className="small mb-1">
              <span className="fw-semibold">{label}</span>
              {f.needs_review && <Badge bg="warning" text="dark" className="ms-2">review</Badge>}
            </div>
            {rows.map((r, i) => (
              <div key={i} className="d-flex align-items-center gap-2 mb-1">
                <div style={{ minWidth: 74 }}>
                  {r.isCurrent ? (
                    // "Keep" only does something while the field is flagged for review
                    // (it dismisses the flag). Once confirmed, it's the current value —
                    // disable it so it reads as already-kept.
                    <Button size="sm" variant="outline-success" disabled={busy || !f.needs_review} onClick={() => acknowledge(f.field)} title={f.needs_review ? 'Keep this value and dismiss the review flag' : 'Already the confirmed value'}>Keep</Button>
                  ) : (
                    <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => acknowledge(f.field, r.value)} title={hasCurrent ? 'Use this value instead' : 'Use this value'}>{hasCurrent ? 'Replace' : 'Use'}</Button>
                  )}
                </div>
                <span className={r.isCurrent ? 'fw-semibold' : ''}>
                  {r.value || <span className="text-muted fst-italic">(blank)</span>}
                </span>
                <span className="d-flex flex-wrap gap-1">
                  {/* No document supplied this value — it was entered directly (manual
                      edit or migrated from the prior system), not extracted from a doc. */}
                  {r.sources.length === 0 && <Badge bg="light" text="dark" className="border" title="Entered directly, not from a document">manual</Badge>}
                  {r.sources.map((s) => (
                    <Badge key={s.document_type} bg="light" text="dark" className="border" title={`${s.count} ${docTypeLabel(s.document_type)}${s.count === 1 ? '' : 's'} with this value`}>
                      {docTypeLabel(s.document_type)}<sup className="ms-1">{s.count}</sup>
                    </Badge>
                  ))}
                </span>
                {/* Only manual (non-document) alternatives can be deleted — the current
                    value and document-supplied values stay. */}
                {!r.isCurrent && r.sources.length === 0 && (
                  <Button size="sm" variant="link" className="text-danger p-0 ms-1 lh-1" disabled={busy} onClick={() => forget(f.field)} title="Delete this manual value">
                    <i className="bi bi-x-lg" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

interface Props {
  applicant: Applicant;
  onChanged?: (a: Applicant) => void;
  onDeleted?: () => void;
}

const ACCEPT = '.pdf,.docx,.txt';

const ApplicantDetailContent: React.FC<Props> = ({ applicant, onChanged, onDeleted }) => {
  const [current, setCurrent] = useState<Applicant>(applicant);
  useEffect(() => setCurrent(applicant), [applicant]);

  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [viewerDoc, setViewerDoc] = useState<ApplicantDocument | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // field editing
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', address: '', linkedin: '', github: '' });
  const [emailRows, setEmailRows] = useState<EmailRow[]>([]);

  // document ops
  const [reassignDoc, setReassignDoc] = useState<ApplicantDocument | null>(null);
  const [reassignTarget, setReassignTarget] = useState<ApplicantOption | null>(null);
  const uploadRef = useRef<HTMLInputElement>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingType, setPendingType] = useState<'resume' | 'cover_letter' | null>(null);

  // While the uploader is open, a file dropped ANYWHERE on the window loads it —
  // no need to hit the drop-zone box (that box is just a click-to-browse target
  // plus a visual highlight during the drag).
  const dragging = usePageFileDrop((files) => {
    if (files.length) setPendingFile(files[0].file);
  }, showUpload);

  const refresh = async () => {
    const fresh = await getApplicantsId(current.id);
    setCurrent(fresh);
    onChanged?.(fresh);
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.response?.data?.error || 'Operation failed');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = () => {
    setForm({
      first_name: current.first_name || '',
      last_name: current.last_name || '',
      phone: current.phone || '',
      address: current.address || '',
      linkedin: current.linkedin || '',
      github: current.github || '',
    });
    setEmailRows(applicantEmails(current).map((e) => ({ id: e.id, email: e.email, is_primary: e.is_primary })));
    setEditing(true);
  };

  // email-row editor helpers
  const setPrimaryEmail = (i: number) => setEmailRows((rows) => rows.map((r, idx) => ({ ...r, is_primary: idx === i })));
  const updateEmail = (i: number, v: string) => setEmailRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, email: v } : r)));
  const addEmail = () => setEmailRows((rows) => [...rows, { email: '', is_primary: rows.length === 0 }]);
  const removeEmail = (i: number) =>
    setEmailRows((rows) => {
      const next = rows.filter((_, idx) => idx !== i);
      if (next.length > 0 && !next.some((r) => r.is_primary)) next[0].is_primary = true;
      return next;
    });

  const saveEdit = () => {
    const cleaned = emailRows.map((r) => ({ ...r, email: r.email.trim() })).filter((r) => r.email);
    if (cleaned.length === 0) {
      setErr('At least one email address is required.');
      return;
    }
    if (!cleaned.some((r) => r.is_primary)) cleaned[0].is_primary = true;
    run(async () => {
      await putApplicantsIdEmails(current.id, cleaned.map((r) => ({ email: r.email, is_primary: r.is_primary })));
      const ops: JsonPatchOperation[] = (Object.keys(form) as (keyof typeof form)[]).map((k) => ({ op: 'add', path: `/${k}`, value: form[k] }));
      await patchApplicantsId(current.id, ops);
      await refresh();
      setEditing(false);
    });
  };

  // Resolve one contact field's conflict: pick a variant (value set) or confirm
  // the current value (value omitted). Both acknowledge the variants on file,
  // clearing the needs-review flag until a new variant arrives.
  const acknowledge = (field: string, value?: string) =>
    run(async () => {
      await postApplicantsIdContactAcknowledge(current.id, value === undefined ? { field } : { field, value });
      await refresh();
    });

  // Delete a field's single manual (non-document) value — a JSON-PATCH remove of that
  // field's contact_manual entry.
  const forget = (field: string) =>
    run(async () => {
      await patchApplicantsId(current.id, [{ op: 'remove', path: `/contact_manual/${field}` }]);
      await refresh();
    });

  const download = async (doc: ApplicantDocument) => {
    setDownloadingId(doc.id);
    try {
      const blob = await getApplicantsIdDocumentsDocumentId(current.id, doc.id);
      const url = URL.createObjectURL(blob as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || `${doc.type}-${doc.id}`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingId(null);
    }
  };

  const deleteDoc = (doc: ApplicantDocument) =>
    run(async () => {
      // eslint-disable-next-line no-restricted-globals, no-alert
      if (!window.confirm(`Delete “${doc.file_name}”? This removes the file.`)) return;
      await deleteApplicantsIdDocumentsDocumentId(current.id, doc.id);
      await refresh();
    });

  const doReassign = () =>
    run(async () => {
      if (!reassignDoc || !reassignTarget) return;
      await postApplicantsIdDocumentsDocumentIdReassign(current.id, reassignDoc.id, { applicant_id: reassignTarget.value });
      setReassignDoc(null);
      setReassignTarget(null);
      await refresh();
    });

  const deleteApplicant = () =>
    run(async () => {
      const n = current.documents?.length ?? 0;
      // eslint-disable-next-line no-restricted-globals, no-alert
      if (!window.confirm(`Delete ${current.email} and ${n} attached file${n === 1 ? '' : 's'} from disk? This cannot be undone.`)) return;
      await deleteApplicantsId(current.id);
      onDeleted?.();
    });

  const openUpload = () => {
    setPendingFile(null);
    setPendingType(null);
    setShowUpload(true);
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (uploadRef.current) uploadRef.current.value = '';
    if (file) setPendingFile(file);
  };

  const confirmUpload = () =>
    run(async () => {
      if (!pendingFile || !pendingType) return;
      await postApplicantsIdDocuments(current.id, { file: pendingFile, doc_type: pendingType });
      setShowUpload(false);
      await refresh();
    });

  const linkField = (label: string, url?: string | null) => (
    <Col md={6} className="mb-2">
      <div className="text-muted small">{label}</div>
      <div className="text-truncate">{url ? <a href={url} target="_blank" rel="noreferrer">{url}</a> : ''}</div>
    </Col>
  );
  const field = (label: string, value?: string) => (
    <Col md={6} className="mb-2">
      <div className="text-muted small">{label}</div>
      <div>{value || ' '}</div>
    </Col>
  );
  const editField = (label: string, k: keyof typeof form) => (
    <Col md={6} className="mb-2">
      <Form.Label className="text-muted small mb-0">{label}</Form.Label>
      <Form.Control size="sm" value={form[k]} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} />
    </Col>
  );

  return (
    <>
      {err && <Alert variant="danger" dismissible onClose={() => setErr(null)} className="py-2">{err}</Alert>}

      <Card className="mb-3">
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <span>
            {[current.first_name, current.last_name].filter(Boolean).join(' ') || current.email}
            {current.contact_conflict && (
              <Badge bg="warning" text="dark" className="ms-2"><i className="bi bi-exclamation-triangle me-1" />Needs review</Badge>
            )}
          </span>
          {editing ? (
            <span className="d-flex gap-2">
              <Button size="sm" variant="light" disabled={busy} onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="sm" variant="success" disabled={busy} onClick={saveEdit}>{busy ? <Spinner animation="border" size="sm" /> : 'Save'}</Button>
            </span>
          ) : (
            <span className="d-flex gap-2">
              <Button size="sm" variant="light" href={`mailto:${applicantEmails(current).map((e) => e.email).join(',')}`} target="_blank" rel="noreferrer"><i className="bi bi-envelope me-1" />Send email</Button>
              <Button size="sm" variant="light" onClick={startEdit}><i className="bi bi-pencil me-1" />Edit</Button>
              <Button size="sm" variant="danger" disabled={busy} onClick={deleteApplicant}><i className="bi bi-trash me-1" />Delete</Button>
            </span>
          )}
        </Card.Header>
        <Card.Body>
          {editing ? (
            <>
              <Row>
                {editField('First name', 'first_name')}
                {editField('Last name', 'last_name')}
                {editField('Phone', 'phone')}
                {editField('Address', 'address')}
                {editField('LinkedIn', 'linkedin')}
                {editField('GitHub', 'github')}
              </Row>
              <div className="mt-2">
                <div className="text-muted small mb-1">Email addresses <span className="fst-italic">(select the primary)</span></div>
                {emailRows.map((r, i) => (
                  <div key={i} className="d-flex align-items-center gap-2 mb-1">
                    <Form.Check type="radio" name="applicant-primary-email" checked={r.is_primary} onChange={() => setPrimaryEmail(i)} title="Primary" />
                    <Form.Control size="sm" type="email" value={r.email} onChange={(e) => updateEmail(i, e.target.value)} placeholder="email@example.com" />
                    <Button size="sm" variant="outline-danger" onClick={() => removeEmail(i)} title="Remove"><i className="bi bi-trash" /></Button>
                  </div>
                ))}
                <Button size="sm" variant="outline-secondary" onClick={addEmail}><i className="bi bi-plus-lg me-1" />Add email</Button>
              </div>
            </>
          ) : (
            <>
              <Row>
                <Col md={6} className="mb-2">
                  <div className="text-muted small">{applicantEmails(current).length > 1 ? 'Emails' : 'Email'}</div>
                  <div className="d-flex flex-column">
                    {applicantEmails(current).map((e) => (
                      <span key={e.id} className="text-truncate">
                        <a href={`mailto:${e.email}`} target="_blank" rel="noreferrer">{e.email}</a>
                        {e.is_primary && <Badge bg="light" text="dark" className="ms-1">primary</Badge>}
                      </span>
                    ))}
                  </div>
                </Col>
                {field('Phone', current.phone)}
                {field('Address', current.address)}
                {linkField('LinkedIn', current.linkedin)}
                {linkField('GitHub', current.github)}
                {field('First Applied', fmtDate(current.first_applied_at))}
                {field('Last Applied', fmtDate(current.last_applied_at))}
                {field('Contact Checked', fmtDate(current.contact_checked_at))}
              </Row>
              <div className="mt-2">
                <Badge bg="secondary" className="me-2">Resumes: {current.resume_count}</Badge>
                <Badge bg="secondary">Cover letters: {current.cover_count}</Badge>
              </div>
              {renderContactReview(current.contact_fields, busy, acknowledge, forget)}
            </>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <span>Documents</span>
          <Button size="sm" variant="light" disabled={busy} onClick={openUpload}><i className="bi bi-upload me-1" />Upload</Button>
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Type</th>
                <th>Source</th>
                <th>File</th>
                <th>Applied</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(current.documents || []).map((d) => (
                <tr key={d.id}>
                  <td>{d.type}</td>
                  <td>{d.source}</td>
                  <td className="text-truncate" style={{ maxWidth: 280 }}>
                    <a href="#" onClick={(e) => { e.preventDefault(); setViewerDoc(d); }}>{d.file_name}</a>
                  </td>
                  <td>{fmtDate(d.applied_at)}</td>
                  <td className="text-end text-nowrap">
                    <Button size="sm" variant="outline-primary" className="me-1" disabled={downloadingId === d.id} onClick={() => download(d)} title="Download">
                      {downloadingId === d.id ? <Spinner animation="border" size="sm" /> : <i className="bi bi-download" />}
                    </Button>
                    <Button size="sm" variant="outline-secondary" className="me-1" disabled={busy} onClick={() => { setReassignTarget(null); setReassignDoc(d); }} title="Reassign">
                      <i className="bi bi-arrow-left-right" />
                    </Button>
                    <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => deleteDoc(d)} title="Delete">
                      <i className="bi bi-trash" />
                    </Button>
                  </td>
                </tr>
              ))}
              {(current.documents || []).length === 0 && (
                <tr><td colSpan={5} className="text-muted text-center py-3">No documents.</td></tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <ApplicantDocPreview doc={viewerDoc} onClose={() => setViewerDoc(null)} />

      <Modal show={showUpload} onHide={() => setShowUpload(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Upload document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <input ref={uploadRef} type="file" accept={ACCEPT} className="d-none" onChange={onFilePicked} />
          <div
            role="button"
            onClick={() => uploadRef.current?.click()}
            className={`border border-2 rounded text-center p-4 mb-3 ${dragging ? 'border-primary bg-light text-primary' : 'text-muted'}`}
            style={{ cursor: 'pointer', borderStyle: 'dashed' }}
          >
            {pendingFile ? (
              <>
                <i className="bi bi-file-earmark-check fs-4 d-block mb-1 text-primary" />
                <span className="text-truncate d-block">{pendingFile.name}</span>
                <div className="small">Drop anywhere, or click to replace</div>
              </>
            ) : (
              <>
                <i className="bi bi-cloud-arrow-up fs-4 d-block mb-1" />
                Drop a file anywhere on this window, or click to browse
                <div className="small">PDF, DOCX, or TXT</div>
              </>
            )}
          </div>
          <div className="text-muted small mb-1">Document type <span className="text-danger">*</span></div>
          <ToggleButtonGroup
            type="radio"
            name="upload-doc-type"
            value={pendingType ?? ''}
            onChange={(v) => setPendingType(v as 'resume' | 'cover_letter')}
          >
            <ToggleButton id="upload-type-resume" variant="outline-primary" value="resume">Resume</ToggleButton>
            <ToggleButton id="upload-type-cover" variant="outline-primary" value="cover_letter">Cover letter</ToggleButton>
          </ToggleButtonGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUpload(false)} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={confirmUpload} disabled={busy || !pendingFile || !pendingType}>
            {busy ? <><Spinner animation="border" size="sm" className="me-1" />Uploading…</> : 'Upload'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!reassignDoc} onHide={() => setReassignDoc(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 text-truncate">Reassign “{reassignDoc?.file_name}”</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-1">Move this document to:</div>
          <ApplicantSelect value={reassignTarget} onChange={setReassignTarget} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setReassignDoc(null)} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={doReassign} disabled={!reassignTarget || busy}>
            {busy ? <><Spinner animation="border" size="sm" className="me-1" />Moving…</> : 'Reassign'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ApplicantDetailContent;
